import api from '../../shared/services/api';
import { AxiosProgressEvent } from 'axios';
import { ComprehensiveCorrelationResponse } from './correlationService';

// Re-export the canonical correlation response type (defined in correlationService.ts)
export type { ComprehensiveCorrelationResponse } from './correlationService';

// Types
export interface DocumentSet {
  id: string;
  name: string;
  status: 'pending' | 'processing' | 'ready' | 'submitted' | 'completed' | 'error';
  documents?: Document[];
  relationships?: DocumentRelationship[];
  merged_data?: any;
  confidence_score?: number;
  conflicts?: Conflict[];
  submission_id?: string;
  submission_status?: string;
  created_at: string;
  updated_at: string;
}

export interface Document {
  id: string;
  file_name: string;
  document_type?: string;
  document_type_confidence?: number;
  extracted_data?: any;
  file_size: number;
  status: 'pending' | 'processing' | 'completed' | 'error';
  upload_progress?: number;
}

export interface DocumentRelationship {
  id: string;
  document_a_id: string;
  document_b_id: string;
  relationship_type: string;
  confidence: number;
  matching_fields: string[];
  tier?: 'strong' | 'medium' | 'weak';
}

export interface Conflict {
  field: string;
  values: Array<{
    value: any;
    source: string;
    confidence: number;
  }>;
  resolution?: string;
}

export interface SubmissionResponse {
  success: boolean;
  submission_id?: string;
  status?: string;
  message?: string;
  error?: string;
}

export interface BatchUploadFailure {
  file_name: string;
  error: string;
}

export interface BatchUploadResult {
  succeeded: Document[];
  failed: BatchUploadFailure[];
}

// Matches backend AutoDetectionResponse (schemas/document_set_schemas.py)
export interface AutoDetectionResult {
  relationships_found: number;
  documents_analyzed: number;
  confidence_average: number;
  relationships: DocumentRelationship[];
}

// API Service
class DocumentSetService {
  // Create a new document set
  async createDocumentSet(name: string): Promise<DocumentSet> {
    const response = await api.post('/api/v1/document-sets/create', { name });
    return {
      ...response.data,
      documents: response.data.documents || [],
      relationships: response.data.relationships || [],
    };
  }

  // Get all document sets for the current user
  async getDocumentSets(): Promise<DocumentSet[]> {
    const response = await api.get('/api/v1/document-sets');
    // Handle paginated response structure
    let sets: any[];
    if (response.data && response.data.sets && Array.isArray(response.data.sets)) {
      sets = response.data.sets;
    } else {
      sets = Array.isArray(response.data) ? response.data : [];
    }
    return sets.map((s: any) => ({
      ...s,
      documents: s.documents || [],
      relationships: s.relationships || [],
    }));
  }

  // Get a specific document set
  async getDocumentSet(setId: string): Promise<DocumentSet> {
    const response = await api.get(`/api/v1/document-sets/${setId}`);
    return response.data;
  }

  // Delete a document set
  async deleteDocumentSet(setId: string): Promise<void> {
    await api.delete(`/api/v1/document-sets/${setId}`);
  }

  // Transform backend document set member to frontend Document type
  // NOTE: file_id is the canonical Document.id — the backend keys
  // remove-document and relationship lookups on the uploaded file id,
  // not the membership row id (which member.id refers to).
  private transformMember(member: any): Document {
    return {
      id: member.file_id || member.id,
      file_name: member.filename || member.file_name || 'unknown',
      document_type: member.document_type,
      document_type_confidence: member.extraction_confidence,
      extracted_data: member.extracted_data,
      file_size: member.file_size || 0,
      status: member.processed_at ? 'completed' : 'pending',
    };
  }

  // Add document to set with progress tracking
  async addDocumentToSet(
    setId: string,
    file: File,
    onProgress?: (progress: number) => void
  ): Promise<Document> {
    const formData = new FormData();
    formData.append('file', file);

    const response = await api.post(
      `/api/v1/document-sets/${setId}/add-document`,
      formData,
      {
        onUploadProgress: (progressEvent: AxiosProgressEvent) => {
          if (progressEvent.total && onProgress) {
            const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            onProgress(progress);
          }
        },
      }
    );

    // Backend returns DocumentSetDetailResponse — extract the last added document
    const docs = response.data?.documents || [];
    if (docs.length > 0) {
      return this.transformMember(docs[docs.length - 1]);
    }
    // Fallback: construct Document from file info
    return {
      id: response.data?.id || file.name,
      file_name: file.name,
      file_size: file.size,
      status: 'completed',
    };
  }

  // Add multiple documents sequentially.
  // Processes ALL files even if some fail, returning per-file results
  // so callers can report partial success.
  async addMultipleDocuments(
    setId: string,
    files: File[],
    onProgress?: (fileIndex: number, progress: number) => void
  ): Promise<BatchUploadResult> {
    const succeeded: Document[] = [];
    const failed: BatchUploadFailure[] = [];

    for (let i = 0; i < files.length; i++) {
      try {
        const doc = await this.addDocumentToSet(setId, files[i], (progress) => {
          if (onProgress) {
            onProgress(i, progress);
          }
        });
        succeeded.push(doc);
      } catch (error: any) {
        failed.push({
          file_name: files[i].name,
          error: error?.response?.data?.detail || error?.message || 'Upload failed',
        });
      }
    }

    return { succeeded, failed };
  }

  // Remove document from set
  async removeDocument(setId: string, documentId: string): Promise<void> {
    await api.post(`/api/v1/document-sets/${setId}/remove-document/${documentId}`);
  }

  // Link already-uploaded file to document set
  async linkFileToSet(
    setId: string,
    fileId: string,
    documentType?: string,
    isPrimary: boolean = false
  ): Promise<any> {
    const response = await api.post(
      `/api/v1/document-sets/${setId}/link-document`,
      {
        file_id: fileId,
        document_type: documentType,
        is_primary: isPrimary
      }
    );
    return response.data;
  }

  // Normalize raw backend relationships: matching_fields may arrive as a
  // Dict<string, Any> while the frontend expects string[]
  private normalizeRelationships(rawRelationships: any): DocumentRelationship[] {
    if (!Array.isArray(rawRelationships)) return [];
    return rawRelationships.map((rel: any) => ({
      id: rel.id,
      document_a_id: rel.document_a_id,
      document_b_id: rel.document_b_id,
      relationship_type: rel.relationship_type,
      confidence: rel.confidence,
      matching_fields: Array.isArray(rel.matching_fields)
        ? rel.matching_fields
        : Object.keys(rel.matching_fields || {}),
    }));
  }

  // Run relationship auto-detection.
  // Backend returns AutoDetectionResponse:
  // { relationships_found, documents_analyzed, confidence_average, relationships }
  async autoDetectDocuments(setId: string): Promise<AutoDetectionResult> {
    const response = await api.post(`/api/v1/document-sets/${setId}/auto-detect`);
    const data = response.data || {};
    return {
      relationships_found: data.relationships_found ?? 0,
      documents_analyzed: data.documents_analyzed ?? 0,
      confidence_average: data.confidence_average ?? 0,
      relationships: this.normalizeRelationships(data.relationships),
    };
  }

  // Combine documents in set
  async combineDocuments(setId: string): Promise<{
    merged_data: any;
    confidence_score: number;
    conflicts: Conflict[];
  }> {
    const response = await api.post(`/api/v1/document-sets/${setId}/combine`, {});
    const data = response.data;
    return {
      merged_data: data.merged_data || {},
      confidence_score: data.confidence_score ?? data.confidence ?? 0,
      conflicts: data.conflicts || [],
    };
  }

  // Auto-detect relationships between documents using AI
  // (same /auto-detect endpoint as autoDetectDocuments; returns just the relationships)
  async getRelationships(setId: string): Promise<DocumentRelationship[]> {
    const result = await this.autoDetectDocuments(setId);
    return result.relationships;
  }

  // Resolve conflicts
  async resolveConflicts(
    setId: string,
    resolutions: Array<{ field: string; selected_value: any }>
  ): Promise<any> {
    const response = await api.post(`/api/v1/document-sets/${setId}/resolve-conflicts`, {
      resolutions,
    });
    return response.data;
  }

  // Validate combined data
  async validateCombination(setId: string): Promise<{
    valid: boolean;
    errors: string[];
    warnings: string[];
  }> {
    const response = await api.post(`/api/v1/document-sets/${setId}/validate`);
    return response.data;
  }

  // Submit to BEO ATLAS
  async submitToAtlas(
    setId: string,
    testMode: boolean = false,
    customReference?: string
  ): Promise<SubmissionResponse> {
    const response = await api.post(`/api/v1/document-sets/${setId}/submit-atlas`, {
      test_mode: testMode,
      custom_reference: customReference,
    });
    return response.data;
  }

  // ========== NEW: Multi-Document Processing Methods ==========

  /**
   * Process and merge all documents in the set with parallel processing
   *
   * This orchestrates:
   * - Parallel OCR and extraction for all documents
   * - Intelligent field merging with priority rules
   * - Conflict detection and auto-resolution
   * - Correlation validation
   *
   * @param setId - Document set ID
   * @param options - Processing options
   * @returns Complete processing result with merged data and conflicts
   */
  async processAndMerge(
    setId: string,
    options: {
      auto_resolve_conflicts?: boolean;
      validate_correlation?: boolean;
      declaration_type?: 'export' | 'import' | 'transit';
      target_module?: string;
    } = {}
  ): Promise<MultiDocumentProcessingResult> {
    const response = await api.post<MultiDocumentProcessingResult>(
      `/api/v1/document-sets/${setId}/process-and-merge`,
      {
        auto_resolve_conflicts: options.auto_resolve_conflicts ?? true,
        validate_correlation: options.validate_correlation ?? true,
        declaration_type: options.declaration_type ?? 'export',
        target_module: options.target_module ?? 'BEO_ATLAS'
      }
    );
    return response.data;
  }

  /**
   * Manually resolve a single field conflict
   *
   * @param setId - Document set ID
   * @param fieldPath - Dot-notation field path (e.g., "kopf.datum")
   * @param selectedIndex - Index of chosen alternative (0-based)
   * @param userComment - Optional comment explaining the choice
   * @returns Resolution result
   */
  async resolveConflict(
    setId: string,
    fieldPath: string,
    selectedIndex: number,
    userComment?: string
  ): Promise<{
    success: boolean;
    field_path: string;
    selected_value: any;
    remaining_conflicts: number;
    resolution_record: any;
  }> {
    const response = await api.post(
      `/api/v1/document-sets/${setId}/resolve-conflict`,
      {
        field_path: fieldPath,
        selected_alternative_index: selectedIndex,
        user_comment: userComment
      }
    );
    return response.data;
  }

  /**
   * Resolve multiple conflicts at once
   *
   * Supports two modes:
   * 1. Auto-apply all recommendations
   * 2. Bulk manual selections
   *
   * @param setId - Document set ID
   * @param options - Resolution options
   * @returns Bulk resolution result
   */
  async resolveAllConflicts(
    setId: string,
    options: {
      apply_recommendations?: boolean;
      resolutions?: Record<string, number>;
      user_comment?: string;
    }
  ): Promise<{
    success: boolean;
    conflicts_resolved: number;
    remaining_conflicts: number;
    updated_merged_data: any;
    resolution_records: any[];
  }> {
    const response = await api.post(
      `/api/v1/document-sets/${setId}/resolve-all-conflicts`,
      {
        apply_recommendations: options.apply_recommendations ?? false,
        resolutions: options.resolutions ?? {},
        user_comment: options.user_comment
      }
    );
    return response.data;
  }

  /**
   * Validate document correlation
   *
   * @param setId - Document set ID
   * @param options - Validation options
   * @returns Comprehensive correlation validation result
   */
  async validateCorrelation(
    setId: string,
    options: {
      declaration_type?: 'export' | 'import' | 'transit';
      target_module?: string;
    } = {}
  ): Promise<ComprehensiveCorrelationResponse> {
    const response = await api.post<ComprehensiveCorrelationResponse>(
      `/api/v1/document-sets/${setId}/validate-correlation`,
      {
        declaration_type: options.declaration_type ?? 'export',
        target_module: options.target_module ?? 'BEO_ATLAS'
      }
    );
    return response.data;
  }
}

// ========== NEW: TypeScript Interfaces for Multi-Document Processing ==========

export interface FieldSource {
  document_id: string;
  document_name: string;
  document_type: string;
  confidence: number;
  extracted_at: string;
  extraction_method: string;
}

export interface ConflictAlternative {
  value: any;
  source: FieldSource;
  confidence: number;
  reasoning: string;
  normalized_value?: any;
  similarity_to_others?: number;
}

export interface FieldConflict {
  field_path: string;
  field_label: string;
  field_category: string;
  conflict_type: 'value_mismatch' | 'format_difference' | 'unit_difference' | 'precision_difference' | 'language_difference';
  severity: 'critical' | 'warning' | 'info';
  alternatives: ConflictAlternative[];
  recommended_value_index: number;
  resolution_strategy: string;
  can_auto_resolve: boolean;
  auto_resolved: boolean;
  resolution_reasoning: string;
  user_attention_required: boolean;
}

export interface MergedFieldValue {
  value: any;
  primary_source: FieldSource;
  all_sources: FieldSource[];
  has_conflict: boolean;
  conflict?: FieldConflict;
  confidence: number;
  is_auto_resolved: boolean;
  is_user_modified: boolean;
}

export interface SingleDocumentResult {
  document_id: string;
  file_id: string;
  filename: string;
  document_type: string;
  ocr_text: string;
  extracted_data: Record<string, any>;
  extraction_confidence: number;
  processing_time_ms: number;
  ocr_time_ms: number;
  extraction_time_ms: number;
  error?: string;
  success: boolean;
}

export interface MultiDocumentProcessingResult {
  success: boolean;
  document_set_id: string;
  documents_processed: number;
  documents_failed: number;
  document_results: SingleDocumentResult[];
  merged_data: Record<string, any>;
  field_metadata: Record<string, MergedFieldValue>;
  conflicts: FieldConflict[];
  total_conflicts: number;
  auto_resolved_conflicts: number;
  manual_resolution_required: number;
  total_fields_extracted: number;
  fields_with_multiple_sources: number;
  average_field_confidence: number;
  processing_time_ms: number;
  ocr_time_ms: number;
  extraction_time_ms: number;
  merging_time_ms: number;
  conflict_detection_time_ms: number;
  correlation_result?: any;
  processed_at: string;
  processing_metadata: Record<string, any>;
}

export const documentSetService = new DocumentSetService();
export default documentSetService;